import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import PropTypes from 'prop-types';
import { Box, CircularProgress, Typography } from '@mui/material';
import {
  ErrorInfoDialogContent,
  FetchedMailList,
  FetchedMailsDialogContent,
  FilterPopUpHeader,
  FilterPopupCloseIcon,
  Root,
  StyledDataGrid,
  StyledFilterPopup,
  classes,
} from './utils';
import fetchEmails from '../../../../services/microsoftGraphApi';
import { CancelButton, SaveButton } from '../flow-configuration/utils';
import fetchAttachmentFileNamesForOutlook from '../../../../services/fetchAttachmentFileNamesForOutlook';
import { manualUploadDocumentFromMicrosoft } from '../../../../services/uploadApi';
import Toast from '../../utils/Toast';
import fetchAllEmails, { uploadFetchedAttachments } from './gmailUpload';
// import { setIsUploaded } from '../../../../store/reloadOnUploadSlice';
import { connectSocket } from '../../../../store/socketConfigSlice';
import closeIcon from '../../../../assets/accountSetting-assets/x-circle.svg';
import {
  connectDocumentSocket,
  disconnectDocumentSocket,
} from '../../../../store/documentUploadSlice';
import { setDocumentData } from '../../../../store/newDocumentSlice';
import { setIsUploaded } from '../../../../store/reloadOnUploadSlice';

const MailListPopup = ({
  open,
  handleClose,
  handlePopupClose,
  queryData,
  isGmailSelectedForUpload,
  isOutlookSelectedForUpload,
  accessTokenForGmail,
  accessTokenForOutlook,
  documentType,
  uploadOption,
}) => {
  const dispatch = useDispatch();
  const isConnected = useSelector((state) => state.socketConfig.isConnected);
  const userDetail = useSelector((state) => state.user.user);
  const userId = userDetail?.id;
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [outlookMails, setOutlookMails] = useState([]);
  const [fetchedEmails, setFetchedEmails] = useState([]);
  const [mailsLength, setMailsLength] = useState(0);
  const isUploadDocumentConnected = useSelector(
    (state) => state.documentUploadConfig.isUploadDocumentConnected,
  );

  const uploadDocumentSocket = useSelector(
    (state) => state.latedUploadedDocument.uploadDocumentSocket,
  );
  const outlookUploadAccessToken = sessionStorage.getItem(
    'outlookUploadAccessToken',
  );

  const handleMailPopupClose = () => {
    if (outlookUploadAccessToken) {
      sessionStorage.removeItem('importManualOption');
      sessionStorage.removeItem('outlookUploadAccessToken');
    }
    handlePopupClose();
  };

  useEffect(() => {
    const fetchMailsForOutlook = async () => {
      const fetchedEmailsFromQuery = await fetchEmails(
        queryData,
        accessTokenForOutlook,
      );
      if (fetchedEmailsFromQuery.length > 0) {
        setOutlookMails(fetchedEmailsFromQuery);
        setMailsLength(fetchedEmailsFromQuery.length);
      } else {
        setMailsLength(-1);
      }
    };
    const fetchMailsForGmail = async () => {
      try {
        const allEmails = await fetchAllEmails(accessTokenForGmail, queryData);
        if (allEmails.length > 0) {
          setFetchedEmails(allEmails);
          setMailsLength(allEmails.length);
        } else {
          setMailsLength(-1);
        }
      } catch (e) {
        Toast({
          message: `Failed to fetch emails from Gmail : ${e?.message}`,
          variant: 'error',
          position: { vertical: 'top', horizontal: 'center' },
          autoHideDuration: 4000,
        });
      }
    };
    if (isOutlookSelectedForUpload) {
      fetchMailsForOutlook();
    } else if (isGmailSelectedForUpload) {
      fetchMailsForGmail();
    }
  }, [queryData]);
  const connectSocketIfNeeded = async () => {
    if (!isConnected) {
      dispatch(connectSocket(userId));
    }
  };
  const uploadDocumentsFromOutlook = async ({ files, email }) => {
    if (isUploadDocumentConnected === false) {
      dispatch(connectDocumentSocket({ userId, dispatch }));
    }
    manualUploadDocumentFromMicrosoft({
      accessToken: accessTokenForOutlook,
      documentType,
      documentImportOption: uploadOption,
      files,
      documentImportCredential: email,
    }).then(() => {
      setTimeout(() => {
        dispatch(setIsUploaded((prev) => !prev));
        dispatch(disconnectDocumentSocket());
        dispatch(setDocumentData(null));
      }, 500);
    });
  };

  const handleOutlookUpload = async () => {
    const data = await fetchAttachmentFileNamesForOutlook(
      selectedEmails,
      accessTokenForOutlook,
    );
    if (data?.files?.length > 0) {
      await connectSocketIfNeeded();
      await uploadDocumentsFromOutlook(data);
    }

    handleMailPopupClose();
    handleClose();
  };

  const handleGmailUpload = async () => {
    if (isUploadDocumentConnected === false) {
      dispatch(connectDocumentSocket({ userId, dispatch }));
    }
    const res = await uploadFetchedAttachments(
      accessTokenForGmail,
      selectedEmails,
      documentType,
      uploadOption,
    );
    if (res?.status === 500) {
      dispatch(setIsUploaded((prev) => !prev));
      dispatch(disconnectDocumentSocket());
      dispatch(setDocumentData(null));
    }
    if (res) {
      setTimeout(() => {
        dispatch(disconnectDocumentSocket());
        dispatch(setDocumentData(null));
      }, 500);
      // dispatch(setIsUploaded((prev) => !prev));
    }

    handleMailPopupClose();
    handleClose();
  };
  useEffect(() => {
    if (uploadDocumentSocket) {
      handleMailPopupClose();
      handleClose();
    }
  }, [uploadDocumentSocket]);
  const handleSaveChanges = async () => {
    if (selectedEmails?.length === 0) {
      return Toast({
        message: 'Select any mail for uploading.',
        variant: 'warning',
      });
    }

    if (isOutlookSelectedForUpload) {
      await handleOutlookUpload();
    } else if (isGmailSelectedForUpload) {
      await handleGmailUpload();
    }
  };

  const columns = [
    {
      field: 'from',
      headerName: 'From',
      width: 200,
      flex: 1,
      headerClassName: 'custom_header',
    },
    {
      field: 'subject',
      headerName: 'Subject',
      width: 200,
      flex: 1,
      headerClassName: 'custom_header',
    },
    {
      field: 'fileName',
      headerName: 'File Name',
      width: 150,
      flex: 1,
      headerClassName: 'custom_header',
    },
  ];

  const outlookRows = outlookMails.slice(0, mailsLength).map((mail) => ({
    ...mail,
    id: mail.id,
    from: mail?.from?.emailAddress?.address,
    subject: mail?.subject,
    fileName: mail?.attachments.map((attachment) => attachment.name).join(', '),
  }));
  const gmailRows = fetchedEmails.slice(0, mailsLength).map((mail) => ({
    id: mail.id,
    from: mail?.from,
    subject: mail?.subject,
    fileName: mail?.filename,
    ...mail,
  }));

  const rows = isOutlookSelectedForUpload ? outlookRows : gmailRows;

  const handleSelectionModelChange = (selectionModel) => {
    const selectedRows = (
      isOutlookSelectedForUpload ? outlookRows : gmailRows
    ).filter((row) => selectionModel.includes(row.id));
    setSelectedEmails(selectedRows);
  };

  return (
    <Root>
      <StyledFilterPopup
        onClose={handleMailPopupClose}
        aria-labelledby="customized-dialog-title"
        open={open}
      >
        <Box className={classes.header}>
          <FilterPopUpHeader id="customized-dialog-title">
            Filtered Emails
            <FilterPopupCloseIcon onClick={handleClose} src={closeIcon} />
          </FilterPopUpHeader>
        </Box>
        {mailsLength === 0 ? (
          <ErrorInfoDialogContent>
            <CircularProgress />
          </ErrorInfoDialogContent>
        ) : (
          <FetchedMailsDialogContent>
            <FetchedMailList className={classes.custom_header}>
              <StyledDataGrid
                rows={rows}
                columns={columns}
                checkboxSelection
                rowSelectionModel={selectedEmails.map((row) => row.id)}
                onRowSelectionModelChange={(itm) =>
                  handleSelectionModelChange(itm)
                }
              />
            </FetchedMailList>
            {/* </NotificationListBox> */}
          </FetchedMailsDialogContent>
        )}
        {mailsLength === -1 ? (
          <ErrorInfoDialogContent>
            <DialogTitle id="customized-dialog-title">
              No Emails found
            </DialogTitle>
          </ErrorInfoDialogContent>
        ) : null}
        <DialogActions>
          <Box className={classes.actionButtonBox}>
            <CancelButton
              className={classes.cancelButton}
              onClick={handleMailPopupClose}
            >
              <Typography variant="body1">Edit Query</Typography>
            </CancelButton>
            <SaveButton
              variant="contained"
              onClick={handleSaveChanges}
              className={classes.saveButton}
            >
              <Typography variant="body1">Upload</Typography>
            </SaveButton>
          </Box>
        </DialogActions>
      </StyledFilterPopup>
    </Root>
  );
};

MailListPopup.propTypes = {
  open: PropTypes.bool.isRequired,
  accessTokenForGmail: PropTypes.string.isRequired,
  accessTokenForOutlook: PropTypes.string.isRequired,
  isOutlookSelectedForUpload: PropTypes.bool.isRequired,
  isGmailSelectedForUpload: PropTypes.bool.isRequired,
  handlePopupClose: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  queryData: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        filter: PropTypes.string.isRequired,
        filterName: PropTypes.string.isRequired,
        condition: PropTypes.string,
        inputValue: PropTypes.string.isRequired,
      }),
    ),
  ).isRequired,
  documentType: PropTypes.string.isRequired,
  uploadOption: PropTypes.string.isRequired,
};

export default MailListPopup;
